import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import CustomModal from '../components/customModal';
import { formatNewMoney, formatRut, cleanRut, rutWithDash } from '../../../utils/formatter';
import { Form, Input, Icon, Dropdown } from 'semantic-ui-react';
import CurrencyDropdown from '../../general/js/currencyDropdown/currencyDropdown';
import { useSelector, useDispatch } from 'react-redux';
import { FULL_ACCESS_PLANS, Plans } from '../../../utils/plans';
import { FindAgenda } from '../../../graphql/agenda';
import { FindCompanyName } from '../../../graphql/addCompany';
import ObligationsDropdown from '../../../containers/ObligationsDropdown';
import moment from 'moment';
import { generateKey } from '../../../utils/generateKey';
import { TransactionType } from '../../../utils/enum';
import { CreateObligation, FindExistingObligation } from '../../../graphql/obligations';
import { CreateAgenda } from '../../../graphql/agenda';
import { GenerateMatch, GenerateMultipleMatch } from '../../../graphql/conciliacion';
import { ModifyDocument } from '../../../graphql/documents';
import { GetIndicatorByDate } from '../../../graphql/indicators';
import WeakCandidate from '../../general/js/forms/reconcile/components/WeakCandidate';
import { useHistory, useLocation } from 'react-router-dom';
import { importAlertState } from '../../../redux/actions/alert';
import { setUpdateDocuments } from 'src/redux/actions/documentostributarios';
import { importUpdateCashFlow, importUpdateCashflowNeeded } from 'src/redux/actions/flujocaja';
import { importEERRUpdateNeeded } from 'src/redux/actions/eerr';
import { importUpdateTransactions } from '../../../redux/actions/conciliacion';
import { importUpdateProvidersData } from 'src/redux/actions/proveedores';
import DefaultObligationFormFields from './DefaultObligationFormFields';
import InvoiceObligationFormFields from './InvoiceObligationFormFields';
import CurrencyChangeRow from '../components/CurrencyChangeRow';
import PorCobrarPorPagarToggle from './PorCobrarPorPagarToggle';
import ObligationCurrencySelector from './ObligationCurrencySelector';
import { getName } from '../../general/js/forms/reconcile/components/matchRowHelpers';
import { documentsDic } from '../../../utils/dataDictionary';
import './createObligationModal.scss';
import { useBlockedFeatures } from 'src/hooks/useBlockedFeatures';

const baseOptions = {
  id: "add",
  key: "add",
  value: "add",
  text: "Crear Contraparte",
  label: "(+)",
};

// Currency to country mapping
const CURRENCY_TO_COUNTRY = {
  'CLP': 'Chile',
  'USD': 'Estados Unidos',
  'EUR': 'Europa',
  'MXN': 'México',
  'ARS': 'Argentina',
  'CNY': 'China',
  'JPY': 'Japón',
  'PEN': 'Perú',
  'BRL': 'Brazil',
  'COP': 'Colombia',
  'CAD': 'Canadá',
  'GBP': 'Reino Unido',
  'UF': 'Chile',
  'UTM': 'Chile',
};

// Helper function to get country from currency code
const getCountryFromCurrency = (currency) => {
  return CURRENCY_TO_COUNTRY[currency] || null;
};

/** YYYY-MM-DD for date input; same priority as pre-fill reconcile: stringDate (DD/MM), else date, else documentDate. */
const resolveFechaFromInitialData = (data) => {
  if (!data) return '';
  if (data.stringDate) {
    const dateMoment = moment(data.stringDate, 'DD/MM/YYYY');
    return dateMoment.isValid() ? dateMoment.format('YYYY-MM-DD') : '';
  }
  if (data.date != null && data.date !== '') {
    return moment(data.date).format('YYYY-MM-DD');
  }
  if (data.documentDate != null && data.documentDate !== '') {
    return moment(data.documentDate).format('YYYY-MM-DD');
  }
  return '';
};

const isBankTransactionType = (data) => {
  if (!data) return false;
  const t = data.type;
  return (
    t === TransactionType.SPEND ||
    t === TransactionType.RECIEVED ||
    t === 'SPEND' ||
    t === 'RECIEVED' ||
    t === 'RECEIVED'
  );
};

/** Reconcile row is a tax/document (not a bank transaction line). */
const isSourceDocumentRow = (data) => {
  if (!data || isBankTransactionType(data)) return false;
  return !!(data.documentType || data.meta?.documentType);
};

/** Same priority as documentInfoBar + top-level documentId/folio. */
const resolveSourceDocumentNumber = (data) => {
  if (!data) return '';
  const v =
    data.meta?.documentId ??
    data.meta?.number ??
    data.documentId ??
    data.folio;
  if (v === null || v === undefined || v === '') return '';
  return String(v);
};

const normalizePartyRut = (rut) => {
  if (rut === null || rut === undefined || rut === '') return '';
  const cleaned = cleanRut(String(rut));
  if (!cleaned || cleaned.length < 8) return '';
  return rutWithDash(cleaned);
};

/** First reconcile document row for party fallback (single or multi-document initialData). */
const resolvePartySourceDocument = (initialData) => {
  if (!initialData) return null;
  const isMultipleDocuments =
    initialData.isMultipleDocuments === true ||
    (Array.isArray(initialData.selectedDocuments) && initialData.selectedDocuments.length > 1);
  if (isMultipleDocuments) {
    const first = (initialData.selectedDocuments || [])[0];
    return first && isSourceDocumentRow(first) ? first : null;
  }
  return isSourceDocumentRow(initialData) ? initialData : null;
};

/** First bank movement row for party fallback (single or multi-transaction initialData). */
const resolvePartySourceBankTransaction = (initialData) => {
  if (!initialData) return null;
  const isMultipleTransactions =
    initialData.isMultiple === true ||
    (Array.isArray(initialData.selectedTransactions) &&
      initialData.selectedTransactions.length > 1);
  if (isMultipleTransactions) {
    const first = (initialData.selectedTransactions || [])[0];
    return first && isBankTransactionType(first) ? first : null;
  }
  return isBankTransactionType(initialData) ? initialData : null;
};

/** Display label for ContraparteDropdown: "RUT :: Nombre". */
const buildContraparteLabel = (rut, name) => {
  const cleaned = cleanRut(String(rut || ''));
  if (!cleaned || cleaned.length < 8) return name || '';
  const formatted = formatRut(cleaned);
  return name ? `${formatted} :: ${name}` : formatted;
};

/** Bank movement: RECIEVED → issuer, SPEND → receiver. */
const extractContraparteFromBankTransaction = (tx) => {
  if (!tx || !isBankTransactionType(tx)) return { rut: '', name: '' };
  const isSpend = tx.type === TransactionType.SPEND || tx.type === 'SPEND';
  if (isSpend) {
    return {
      rut: tx.receiver || tx.meta?.receiverId || '',
      name: tx.receiverName || tx.meta?.receiverName || '',
    };
  }
  return {
    rut: tx.issuer || tx.meta?.issuerId || '',
    name: tx.issuerName || tx.meta?.issuerName || '',
  };
};

/** Document row: Por Pagar (isReceived) → receiver, Por Cobrar → issuer. */
const extractContraparteFromDocument = (doc, isReceived) => {
  if (!doc) return { rut: '', name: '' };
  if (isReceived) {
    return {
      rut: doc.receiver || doc.meta?.receiverId || '',
      name: doc.receiverName || doc.meta?.receiverName || '',
    };
  }
  return {
    rut: doc.issuer || doc.meta?.issuerId || '',
    name: doc.issuerName || doc.meta?.issuerName || '',
  };
};

const CreateObligationModal = ({
  open,
  onClose,
  initialData,
  onSuccess,
  preSelectedObligationType,
  isPaymentMethod,
  obligationCopyMode = false,
}) => {
  const { t } = useTranslation('configuracion');
  const [formData, setFormData] = useState({
    obligationType: '', // Store the type (string key) as value
    obligation: null, // Store the full obligation object
    recurrence: 'unica',
    fecha: moment().format('YYYY-MM-DD'),
    desdeDate: '',
    hastaDate: '',
    repeat: 30,
    amount: '',
    originalAmount: '',
    currency: 'CLP',
    rate: 1,
    contraparte: '',
    contraparteId: '',
    contraparteRut: '',
    contraparteName: '',
    documentId: '',
    description: '',
    cashflowAreaId: '',
    fieldKey: '',
    referenceNumber: '',
    invoiceCurrency: '',
    invoiceExchangeRate: '',
    initialIsReceived: null, // Store isReceived from initialData when present
    obligationCurrency: 'CLP', // Currency for non-invoice obligations (CLP, UF, UTM, USD, EUR)
    paymentMethod: false, // Flag for payment method obligations
    diasVencimiento: '30', // Days to maturity for factoring (default: 30)
    factoringName: '', // Factoring company name
    login: '', // Garantía obligations: login (stored in meta)
    transactionNumber: '', // Garantía obligations: transaction number (stored in meta)
    ticket: '' // Garantía obligations: ticket (stored in meta)
  });

  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [foundExistingObligation, setFoundExistingObligation] = useState(null);
  const [userSelectedContraparte, setUserSelectedContraparte] = useState(false);
  const [showMoreCampos, setShowMoreCampos] = useState(false);
  const [shouldFadeIn, setShouldFadeIn] = useState(false);
  const [modalSessionTick, setModalSessionTick] = useState(0);
  const prevOpenRef = useRef(false);
  const prefillGenerationRef = useRef(0);

  // Reset contraparte selection flag only when the modal opens (not on every pre-fill re-run)
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setUserSelectedContraparte(false);
      setModalSessionTick((t) => t + 1);
    }
    prevOpenRef.current = open;
  }, [open]);

  // Get company plans and ID from Redux
  const { selectedCompany: { id: companyId, plan: plan, name: companyName, rut: companyRut } = {} } = useSelector((state) => state.companyList || {});
  const companyPlans = plan || [];
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { isCrearObligacionBlocked, isAbonarBlocked } = useBlockedFeatures();

  useEffect(() => {
    if (!open) return;
    if (isPaymentMethod && isAbonarBlocked()) {
      onClose?.();
      return;
    }
    if (!isPaymentMethod && !obligationCopyMode && isCrearObligacionBlocked()) {
      onClose?.();
    }
  }, [open, isPaymentMethod, obligationCopyMode, isAbonarBlocked, isCrearObligacionBlocked, onClose]);

  // GraphQL mutation hooks
  const { executeMutation, fetching } = CreateObligation();
  const { executeMutation: createAgenda } = CreateAgenda();
  const { executeMutation: generateMatch } = GenerateMatch();
  const { executeMutation: generateMultipleMatch } = GenerateMultipleMatch();
  const { executeMutation: findCompanyName } = FindCompanyName();
  const { executeMutation: modifyDocument } = ModifyDocument();

  const applyContrapartePrefillFromMovement = useCallback((rut, name, prefillGen) => {
    if (initialData?.agendaId || !rut) return;
    const cleanedRut = cleanRut(rut);
    const cleanedCompanyRut = companyRut ? cleanRut(companyRut) : '';
    const isCompanyRut = cleanedRut && cleanedCompanyRut && cleanedRut === cleanedCompanyRut;
    if (cleanedRut.length < 8 || isCompanyRut) return;

    const commitPrefill = (resolvedName) => {
      setFormData((prev) => ({
        ...prev,
        contraparteRut: cleanedRut,
        contraparteName: resolvedName || '',
        contraparte: buildContraparteLabel(cleanedRut, resolvedName),
        contraparteId: '',
      }));
      setUserSelectedContraparte(true);
    };

    if (name) {
      commitPrefill(name);
      return;
    }

    findCompanyName({ rut: cleanedRut }).then(({ data }) => {
      if (prefillGen !== prefillGenerationRef.current) return;
      const resolvedName =
        data?.findCompanyName && data.findCompanyName !== 'no' ? data.findCompanyName : '';
      commitPrefill(resolvedName);
    });
  }, [companyRut, initialData?.agendaId, findCompanyName]);

  // Calculate period from fecha (YYYYMM format)
  const period = formData.fecha ? moment(formData.fecha).format('YYYYMM') : null;
  const obligationType = formData.obligation?.type || null;
  
  // Calculate currency date for exchange rate fetching (same logic as crearObligacion.jsx)
  const currencyDate = formData.fecha 
    ? (moment().isBefore(moment(formData.fecha, 'YYYY-MM-DD'))
        ? moment().format("DD/MM/YYYY")
        : moment(formData.fecha, 'YYYY-MM-DD').format("DD/MM/YYYY"))
    : moment().format("DD/MM/YYYY");
  
  // Fetch exchange rate indicators when CurrencyChangeRow is used
  // Also fetch for non-invoice obligations with non-CLP currency
  const shouldFetchExchangeRate = (formData.invoiceCurrency && formData.invoiceCurrency !== 'CLP') || 
    (formData.obligationCurrency && formData.obligationCurrency !== 'CLP');
  const getIndicatorByDate = GetIndicatorByDate(shouldFetchExchangeRate ? currencyDate : null);
  
  // Query to find existing obligation - only when opened from reconcile (not from payment method dropdown)
  // Don't search if opened from payment method dropdown (isPaymentMethod === true)
  const shouldSearch =
    initialData && obligationType && period && open && !isPaymentMethod && !obligationCopyMode;
  const { data: existingObligationData, fetching: searchingObligation, reexecuteQuery: searchObligation } = FindExistingObligation(
    companyId,
    obligationType || '',
    period || '',
    !shouldSearch // pause if we shouldn't search
  );

  // Query agenda by ID if initialData has agendaId
  const agendaIdToFetch = (open && initialData?.agendaId) ? initialData.agendaId : null;
  const { data: agendaData, fetching: agendaFetching, error: agendaError } = FindAgenda(agendaIdToFetch);

  const contraparteSessionKey = useMemo(() => {
    const tick = `t${modalSessionTick}`;
    if (!initialData) return `no|initial|${tick}`;
    const parts = [
      initialData._id,
      initialData.id,
      initialData.documentId,
      initialData.meta?.documentId,
      initialData.folio,
      initialData.agendaId,
    ].filter((v) => v != null && v !== '');
    const base = parts.map(String).join('|');
    return base ? `${base}|${tick}` : `obligation-context|${tick}`;
  }, [
    modalSessionTick,
    initialData?._id,
    initialData?.id,
    initialData?.documentId,
    initialData?.meta?.documentId,
    initialData?.folio,
    initialData?.agendaId,
  ]);

  // Build transaction description similar to transactionInfoBar
  const buildTransactionDescription = (data) => {
    if (!data) return '';
    
    // Check if this is a document (has documentType) or transaction
    const isDocument = data.documentType && !data.type;
    const isTransaction = data.type === TransactionType.SPEND || 
                          data.type === TransactionType.RECIEVED ||
                          data.type === 'SPEND' ||
                          data.type === 'RECIEVED' ||
                          data.type === 'RECEIVED';
    
    // For documents, use getName helper which has the correct isReceived logic
    if (isDocument) {
      // Ensure documentType is set for getName to work correctly
      const documentData = { ...data, documentType: data.documentType };
      return getName(documentData);
    }
    
    // For transactions, use the original logic
    const transactionDescription = data.description || data.meta?.description || '';
    const isOut = data.type === TransactionType.SPEND;
    
    const receiver = data.receiver || data.meta?.receiverId;
    const receiverName = data.receiverName || data.meta?.receiverName;
    const issuer = data.issuer || data.meta?.issuerId;
    const issuerName = data.issuerName || data.meta?.issuerName;
    
    const name = isOut
      ? receiver ? (`${receiverName ? generateKey(transactionDescription).includes(generateKey(receiverName)) ? '' : receiverName : ''} RUT: ${receiver}`) : ''
      : issuer ? `${issuerName ? generateKey(transactionDescription).includes(generateKey(issuerName)) ? '' : issuerName : ''} RUT: ${issuer}` : '';
    
    return `${transactionDescription} ${name ? '::' : ''} ${name ? name : ''}`.trim();
  };


  // Search for existing obligation when type and date are selected (only when opened from reconcile, not from payment method)
  useEffect(() => {
    // Only search when modal is open, initialData exists (from reconcile), both type and date are selected, and NOT from payment method dropdown
    if (
      open &&
      initialData &&
      obligationType &&
      period &&
      companyId &&
      !isPaymentMethod &&
      !obligationCopyMode
    ) {
      // Trigger the search
      searchObligation({
        requestPolicy: 'network-only',
        variables: {
          companyId,
          type: obligationType,
          period
        }
      });
    } else {
      // Clear found obligation if search conditions are not met
      setFoundExistingObligation(null);
    }
  }, [obligationType, period, open, initialData, companyId, isPaymentMethod, searchObligation]);

  // Update foundExistingObligation when search results come back
  useEffect(() => {
    if (existingObligationData?.findExistingObligation) {
      setFoundExistingObligation(existingObligationData.findExistingObligation);
    } else {
      setFoundExistingObligation(null);
    }
  }, [existingObligationData]);

  // Handle agenda data when agendaId is queried (clear on error / missing / stale id; guard id for cache)
  useEffect(() => {
    if (!open || !initialData?.agendaId || !agendaIdToFetch) return;
    if (String(agendaIdToFetch) !== String(initialData.agendaId)) return;

    const agenda = agendaData?.Agenda;

    const clearContraparteFromAgenda = () => {
      setFormData((prev) => ({
        ...prev,
        contraparte: '',
        contraparteId: '',
        contraparteRut: '',
        contraparteName: '',
      }));
      setUserSelectedContraparte(false);
    };

    if (agenda && String(agenda.id) === String(initialData.agendaId)) {
      const rut = agenda.worldWide === 'cl' ? formatRut(agenda.rut) : agenda.rut;
      const nameAndRut = `${rut} :: ${agenda.name}`;

      setFormData((prev) => ({
        ...prev,
        contraparteId: agenda.id,
        contraparte: nameAndRut,
        contraparteRut: agenda.rut,
        contraparteName: agenda.name,
      }));
      setUserSelectedContraparte(true);
      return;
    }

    if (!agendaFetching) {
      if (agendaError || !agenda) {
        clearContraparteFromAgenda();
      } else if (agenda && String(agenda.id) !== String(initialData.agendaId)) {
        clearContraparteFromAgenda();
      }
    }
  }, [
    agendaData,
    agendaFetching,
    agendaError,
    open,
    initialData?.agendaId,
    agendaIdToFetch,
  ]);

  // Fetch exchange rate when currency or date changes (for CurrencyChangeRow)
  useEffect(() => {
    if (getIndicatorByDate.data?.getIndicatorByDate) {
      // For invoice obligations, use invoiceCurrency
      // For non-invoice obligations, use obligationCurrency (prioritize over invoiceCurrency if it's CLP)
      // If invoiceCurrency is CLP and obligationCurrency is set, use obligationCurrency
      let currency = null;
      if (formData.obligationCurrency && formData.obligationCurrency !== 'CLP') {
        // Non-invoice obligation with non-CLP currency selected
        currency = formData.obligationCurrency;
      } else if (formData.invoiceCurrency && formData.invoiceCurrency !== 'CLP') {
        // Invoice obligation or transaction with non-CLP currency
        currency = formData.invoiceCurrency;
      } else if (formData.currency && formData.currency !== 'CLP') {
        // Fallback to currency
        currency = formData.currency;
      }
      
      // Only update if currency is not CLP and we have exchange data
      if (currency && currency !== 'CLP' && getIndicatorByDate.data.getIndicatorByDate.exchange[currency]) {
        const exchangeRate = getIndicatorByDate.data.getIndicatorByDate.exchange[currency];
        // Only update if invoiceExchangeRate is empty or hasn't been manually set
        if (!formData.invoiceExchangeRate || formData.invoiceExchangeRate === '') {
          setFormData(prev => ({
            ...prev,
            invoiceExchangeRate: exchangeRate.toString()
          }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getIndicatorByDate.data, formData.invoiceCurrency, formData.obligationCurrency, formData.currency, formData.fecha]);

  // Re-execute query when date or currency changes
  useEffect(() => {
    // Prioritize obligationCurrency for non-invoice obligations
    let currency = null;
    if (formData.obligationCurrency && formData.obligationCurrency !== 'CLP') {
      currency = formData.obligationCurrency;
    } else if (formData.invoiceCurrency && formData.invoiceCurrency !== 'CLP') {
      currency = formData.invoiceCurrency;
    } else if (formData.currency && formData.currency !== 'CLP') {
      currency = formData.currency;
    }
    
    if (formData.fecha && currency && currency !== 'CLP') {
      getIndicatorByDate.reexecuteQuery({ requestPolicy: 'network-only' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.fecha, formData.invoiceCurrency, formData.obligationCurrency, formData.currency]);

  // Pre-fill form when initialData is provided (from reconcile)
  useEffect(() => {
    // Reset loading state when modal opens
    if (open) {
      setIsLoading(false);
      setFoundExistingObligation(null);
    }
    
    if (open && initialData && obligationCopyMode) {
      prefillGenerationRef.current += 1;
      const prefillGen = prefillGenerationRef.current;
      const copyPrefill = initialData.copyPrefill || {};
      const meta = initialData.meta || {};

      setFormData((prev) => ({
        ...prev,
        obligationType: '',
        obligation: null,
        originalAmount: copyPrefill.amount || '',
        amount: copyPrefill.amount || '',
        currency: initialData.currency || 'CLP',
        fecha: copyPrefill.fecha || moment().format('YYYY-MM-DD'),
        description: copyPrefill.description || '',
        recurrence: 'unica',
        contraparte: '',
        contraparteId: '',
        contraparteRut: '',
        contraparteName: '',
        documentId: copyPrefill.documentId || '',
        referenceNumber: copyPrefill.referenceNumber || '',
        invoiceCurrency: copyPrefill.invoiceCurrency || prev.invoiceCurrency,
        invoiceExchangeRate: copyPrefill.invoiceExchangeRate || '',
        obligationCurrency: initialData.currency || 'CLP',
        initialIsReceived:
          copyPrefill.initialIsReceived !== undefined
            ? copyPrefill.initialIsReceived
            : meta.isReceived !== undefined
              ? meta.isReceived
              : true,
        cashflowAreaId: initialData.cashflowAreaId || '',
        fieldKey: initialData.fieldKey || '',
        paymentMethod: false,
      }));

      const isReceivedFromCopy =
        copyPrefill.initialIsReceived !== undefined
          ? copyPrefill.initialIsReceived
          : meta.isReceived !== undefined
            ? meta.isReceived
            : true;
      const { rut: contraparteRut, name: contraparteName } = isBankTransactionType(initialData)
        ? extractContraparteFromBankTransaction(initialData)
        : extractContraparteFromDocument(initialData, isReceivedFromCopy);

      applyContrapartePrefillFromMovement(contraparteRut, contraparteName, prefillGen);
    } else if (open && initialData) {
      prefillGenerationRef.current += 1;
      const prefillGen = prefillGenerationRef.current;

      // Check if this is multiple transactions
      const isMultipleTransactions = initialData.isMultiple === true || 
                                     (initialData.selectedTransactions && 
                                      Array.isArray(initialData.selectedTransactions) && 
                                      initialData.selectedTransactions.length > 1);
      
      // Check if this is multiple documents
      const isMultipleDocuments = initialData.isMultipleDocuments === true ||
                                 (initialData.selectedDocuments &&
                                  Array.isArray(initialData.selectedDocuments) &&
                                  initialData.selectedDocuments.length > 1);
      
      let remaining, currency, fecha, description, isReceivedFromInitialData, contraparteRut, contraparteName, exchangeRate;
      
      if (isMultipleDocuments) {
        // Handle multiple documents
        const selectedDocuments = initialData.selectedDocuments || [];
        
        // Sum all remaining amounts
        remaining = selectedDocuments.reduce((sum, doc) => {
          const docRemaining = doc.meta?.remaining !== undefined 
            ? doc.meta.remaining 
            : (doc.remaining !== undefined ? doc.remaining : (doc.amount || doc.meta?.amount || 0));
          return sum + Math.abs(docRemaining);
        }, 0);
        
        // Use currency from first document
        currency = selectedDocuments[0]?.currencyType || selectedDocuments[0]?.currency || 'CLP';
        
        // Set fecha to today for multiple documents
        fecha = moment().format('YYYY-MM-DD');
        
        // Build description from first document
        description = selectedDocuments.length > 0 
          ? buildTransactionDescription(selectedDocuments[0])
          : 'Obligación múltiple';
        
        // Determine isReceived from first document
        const firstDocument = selectedDocuments[0];
        isReceivedFromInitialData = firstDocument?.meta?.isReceived !== undefined 
          ? firstDocument.meta.isReceived 
          : true;
        
        // Extract counterparty from first document
        ({ rut: contraparteRut, name: contraparteName } = extractContraparteFromDocument(
          firstDocument,
          isReceivedFromInitialData
        ));
        
        // Get exchange rate from first document if available
        exchangeRate = firstDocument?.meta?.rate || firstDocument?.rate || '';
      } else if (isMultipleTransactions) {
        // Handle multiple transactions
        const selectedTransactions = initialData.selectedTransactions || [];
        
        // Sum all remaining amounts
        remaining = selectedTransactions.reduce((sum, transaction) => {
          const transactionRemaining = transaction.remaining !== undefined 
            ? transaction.remaining 
            : (transaction.meta?.remaining !== undefined ? transaction.meta?.remaining : (transaction.amount || transaction.meta?.amount || 0));
          return sum + Math.abs(transactionRemaining);
        }, 0);
        
        // Use currency from first transaction (or most common if mixed)
        currency = selectedTransactions[0]?.currency || selectedTransactions[0]?.meta?.currency || 'CLP';
        
        // Set fecha to today for multiple transactions
        fecha = moment().format('YYYY-MM-DD');
        
        // Build description from first transaction (or generic)
        description = selectedTransactions.length > 0 
          ? buildTransactionDescription(selectedTransactions[0])
          : 'Obligación múltiple';
        
        // Determine isReceived from first transaction
        const firstTransaction = selectedTransactions[0];
        const isTransaction = firstTransaction?.type === TransactionType.SPEND || 
                             firstTransaction?.type === TransactionType.RECIEVED ||
                             firstTransaction?.type === 'SPEND' ||
                             firstTransaction?.type === 'RECIEVED' ||
                             firstTransaction?.type === 'RECEIVED';
        
        if (isTransaction) {
          isReceivedFromInitialData = firstTransaction.type === TransactionType.SPEND || firstTransaction.type === 'SPEND';
        } else {
          isReceivedFromInitialData = firstTransaction?.meta?.isReceived !== undefined 
            ? firstTransaction.meta.isReceived 
            : true;
        }
        
        // Extract RUT from first transaction (RECIEVED → issuer, SPEND → receiver)
        ({ rut: contraparteRut, name: contraparteName } = extractContraparteFromBankTransaction(
          firstTransaction
        ));
        
        // Get exchange rate from first transaction if available
        exchangeRate = firstTransaction?.meta?.rate || firstTransaction?.rate || '';
      } else {
        // Handle single transaction (existing logic)
        // Get remaining amount
        remaining = initialData.remaining !== undefined 
          ? initialData.remaining 
          : (initialData.meta?.remaining !== undefined ? initialData.meta?.remaining : (initialData.amount || initialData.meta?.amount || 0));
        
        // Get currency
        currency = initialData.currency || initialData.meta?.currency || initialData.currencyType || 'CLP';
        
        // Get date and convert to YYYY-MM-DD format for date input.
        // Payment method flow: same document/txn date as reconcile when available; today only as fallback.
        const resolvedFecha = resolveFechaFromInitialData(initialData);
        fecha = isPaymentMethod
          ? (resolvedFecha || moment().format('YYYY-MM-DD'))
          : resolvedFecha;
        
        // Build description
        description = buildTransactionDescription(initialData);
        
        // Determine isReceived from initialData
        // If it's a transaction: SPEND → true (Por Pagar), otherwise → false (Por Cobrar)
        // If it's a document: use meta.isReceived
        // Check if initialData is a transaction (has 'type' property with TransactionType values)
        const isTransaction = initialData.type === TransactionType.SPEND || 
                               initialData.type === TransactionType.RECIEVED ||
                               initialData.type === 'SPEND' ||
                               initialData.type === 'RECIEVED' ||
                               initialData.type === 'RECEIVED';
        
        if (isTransaction) {
          // For transactions: SPEND → true (Por Pagar), otherwise → false (Por Cobrar)
          isReceivedFromInitialData = initialData.type === TransactionType.SPEND || initialData.type === 'SPEND';
        } else {
          // For documents: use meta.isReceived (default to true if not present)
          isReceivedFromInitialData = initialData.meta?.isReceived !== undefined 
            ? initialData.meta.isReceived 
            : true;
        }
        
        // Extract counterparty based on data type (transaction vs document)
        if (isTransaction) {
          ({ rut: contraparteRut, name: contraparteName } = extractContraparteFromBankTransaction(
            initialData
          ));
        } else {
          ({ rut: contraparteRut, name: contraparteName } = extractContraparteFromDocument(
            initialData,
            isReceivedFromInitialData
          ));
        }
        
        // Get exchange rate if available
        exchangeRate = initialData.meta?.rate || initialData.rate || '';
      }

      let prefillDocumentId = '';
      if (isMultipleDocuments) {
        const first = (initialData.selectedDocuments || [])[0];
        if (first && isSourceDocumentRow(first)) {
          prefillDocumentId = resolveSourceDocumentNumber(first);
        }
      } else if (!isMultipleTransactions && isSourceDocumentRow(initialData)) {
        prefillDocumentId = resolveSourceDocumentNumber(initialData);
      }
      
      if (isPaymentMethod) {
        const documentLabel = ['1000'].includes(initialData?.documentCode)
          ? initialData?.typeLabel
          : (documentsDic[initialData?.documentCode]?.fullName ||
            documentsDic[initialData?.documentCode]?.label ||
            initialData?.meta?.summary ||
            'Documento');
        const documentWithId = prefillDocumentId
          ? `${documentLabel} #${prefillDocumentId}`
          : documentLabel;
        const emissionDate =
          initialData?.emition ||
          initialData?.date ||
          initialData?.documentDate ||
          (initialData?.stringDate
            ? moment(initialData.stringDate, 'DD/MM/YYYY').toDate()
            : null);
        const emissionDateText = emissionDate && moment(emissionDate).isValid()
          ? moment(emissionDate).format('DD/MM/YYYY')
          : moment().format('DD/MM/YYYY');
        description = `Pago de ${documentWithId} por ${formatNewMoney(Math.abs(remaining || 0))} con fecha de emisión ${emissionDateText}`;
      }

      // Update form data (reset contraparte so prior document/session does not leak)
      setFormData(prev => ({
        ...prev,
        originalAmount: remaining.toString(),
        amount: remaining.toString(),
        currency: currency,
        fecha: fecha,
        description: description,
        recurrence: 'unica', // Always set to 'unica' when opened from transaction
        contraparte: '',
        contraparteId: '',
        contraparteRut: '',
        contraparteName: '',
        // Pre-fill invoice currency and exchange rate if currency is not CLP
        invoiceCurrency: currency !== 'CLP' ? currency : prev.invoiceCurrency,
        invoiceExchangeRate: currency !== 'CLP' && exchangeRate ? exchangeRate.toString() : prev.invoiceExchangeRate,
        // Set obligationCurrency to the currency from initialData so the dropdown shows with the correct value
        obligationCurrency: currency,
        // Store isReceived from initialData
        initialIsReceived: isReceivedFromInitialData,
        documentId: prefillDocumentId || '',
      }));
      
      // Apply counterparty from movement/document when no agendaId
      applyContrapartePrefillFromMovement(contraparteRut, contraparteName, prefillGen);
    } else if (open && !initialData) {
      // Reset form when opening without initialData
      setFormData({
        obligationType: '',
        obligation: null,
        recurrence: 'unica',
        fecha: moment().format('YYYY-MM-DD'),
        desdeDate: '',
        hastaDate: '',
        repeat: 30,
        amount: '',
        originalAmount: '',
        currency: 'CLP',
        rate: 1,
        contraparte: '',
        contraparteId: '',
        contraparteRut: '',
        contraparteName: '',
        documentId: '',
        description: '',
        cashflowAreaId: '',
        fieldKey: '',
        referenceNumber: '',
        invoiceCurrency: '',
        invoiceExchangeRate: '',
        initialIsReceived: null,
        obligationCurrency: 'CLP',
        paymentMethod: false,
        diasVencimiento: '30',
        factoringName: '',
        login: '',
        transactionNumber: '',
        ticket: ''
      });
      // Reset showMoreCampos when modal opens without initialData
      // But keep it open if opened from "Hacer una Estimación" (estimaciondeegreso)
      if (preSelectedObligationType !== 'estimaciondeegreso') {
        setShowMoreCampos(false);
      }
    }
  }, [open, initialData, preSelectedObligationType, isPaymentMethod, obligationCopyMode, applyContrapartePrefillFromMovement]);

  // Handle pre-selected obligation type (from payment method dropdown)
  useEffect(() => {
    if (open && preSelectedObligationType) {
      // Set paymentMethod flag if isPaymentMethod is true
      if (isPaymentMethod) {
        handleInputChange('paymentMethod', true);
      }
      // The ObligationsDropdown will handle pre-selection via the type prop
      // Reset obligationType to allow re-selection when preSelectedObligationType changes
      if (
        !obligationCopyMode &&
        formData.obligationType !== preSelectedObligationType
      ) {
        handleInputChange('obligationType', '');
        handleInputChange('obligation', null);
      }
      // If opened from "Hacer una Estimación" (estimaciondeegreso), open "ver más campos" by default
      if (preSelectedObligationType === 'estimaciondeegreso') {
        setShowMoreCampos(true);
      }
    }
  }, [open, preSelectedObligationType, isPaymentMethod, obligationCopyMode]);

  // Removed agenda search logic - ContraparteDropdown handles its own search

  const onSelectContraparte = (e, { value, text, name, rut, isNew }) => {
    // Mark that user has explicitly selected a contraparte
    setUserSelectedContraparte(true);
    
    // Handle clearing (empty value)
    if (!value) {
      setUserSelectedContraparte(false);
      handleInputChange('contraparteId', '');
      handleInputChange('contraparte', '');
      handleInputChange('contraparteRut', '');
      handleInputChange('contraparteName', '');
      return;
    }
    
    if (isNew) {
      // New contraparte being created
      handleInputChange('contraparteId', 'add');
      handleInputChange('contraparte', text || '');
      handleInputChange('contraparteRut', rut || '');
      handleInputChange('contraparteName', name || '');
    } else {
      // Existing contraparte selected - store RUT and name for API call
      handleInputChange('contraparteId', value);
      handleInputChange('contraparte', text || '');
      handleInputChange('contraparteRut', rut || ''); // Store RUT from dropdown
      handleInputChange('contraparteName', name || ''); // Store name from dropdown
    }
  };

  // Removed handleContraparteRutChange and handleContraparteNameChange - ContraparteDropdown handles this

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleObligationTypeChange = (e, { value, obligation }) => {
    // value is the type (string key), obligation is the full object
    // If initialData exists, use initialIsReceived; otherwise use obligation's isReceived or default to true
    const isReceivedValue = formData.initialIsReceived !== null 
      ? formData.initialIsReceived 
      : (obligation?.isReceived !== undefined ? obligation.isReceived : true);
    
    const obligationWithDefault = {
      ...obligation,
      isReceived: isReceivedValue
    };
    
    // Trigger fade-in effect if "ver más" is already open when obligation type changes
    if (showMoreCampos && formData.obligationType && formData.obligationType !== value) {
      setShouldFadeIn(true);
      // Reset fade-in after animation completes
      setTimeout(() => setShouldFadeIn(false), 500);
    }
    
    handleInputChange('obligationType', value); // Store type as value
    handleInputChange('obligation', obligationWithDefault); // Store full obligation object with default isReceived
    // Clear existing obligation when type changes
    setFoundExistingObligation(null);
    
    // If invoice is selected, set default currency to USD
    const isInvoice = value === 'invoice' || 
                      obligation?.type === 'invoice' ||
                      obligation?.typeLabel === 'Invoice' ||
                      obligation?.typeLabel === 'invoice';
    
    if (isInvoice && !formData.invoiceCurrency && !initialData) {
      // Only set to USD if not already set and not opened from a transaction
      handleInputChange('invoiceCurrency', 'USD');
      handleInputChange('currency', 'USD');
    }
  };

  const handleIsReceivedToggle = (newIsReceived) => {
    // Update the obligation's isReceived property
    if (formData.obligation) {
      handleInputChange('obligation', {
        ...formData.obligation,
        isReceived: newIsReceived
      });
    }
  };

  const handleRecurrenceChange = (e) => {
    const newRecurrence = e.target.value;
    handleInputChange('recurrence', newRecurrence);
    
    // If switching to 'recurrente' and fecha has a value, copy it to desdeDate
    if (newRecurrence === 'recurrente' && formData.fecha) {
      handleInputChange('desdeDate', formData.fecha);
    }
  };

  const handleFechaChange = (e) => {
    handleInputChange('fecha', e.target.value);
    // Clear existing obligation when date changes
    setFoundExistingObligation(null);
    // Clear exchange rate so it can be recalculated with the new date
    handleInputChange('invoiceExchangeRate', '');
  };

  const handleDesdeDateChange = (e) => {
    handleInputChange('desdeDate', e.target.value);
  };

  const handleHastaDateChange = (e) => {
    handleInputChange('hastaDate', e.target.value);
  };

  const handleRepeatChange = (e) => {
    handleInputChange('repeat', parseInt(e.target.value));
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    const isNonCLP = (formData.obligationCurrency && formData.obligationCurrency !== 'CLP') ||
      formData.currency !== 'CLP' ||
      (formData.invoiceCurrency && formData.invoiceCurrency !== 'CLP');

    if (isNonCLP) {
      // Non-CLP: allow one decimal separator (comma or dot), e.g. "145,56" or "145.56"
      const normalized = value.replace(',', '.');
      if (normalized === '' || /^\d*\.?\d*$/.test(normalized)) {
        const amountStr = normalized === '' ? '' : normalized;
        handleInputChange('originalAmount', amountStr);
        const num = parseFloat(normalized || 0);
        const rate = parseFloat(formData.invoiceExchangeRate || formData.rate || 0);
        if (rate > 0) {
          handleInputChange('amount', Math.round(num * rate));
        } else {
          handleInputChange('amount', amountStr);
        }
      }
    } else {
      // CLP: remove dots (thousands separator), digits only (e.g. "236.404" → "236404")
      const cleanedValue = value.replace(/\./g, '');
      if (cleanedValue === '' || /^\d+$/.test(cleanedValue)) {
        handleInputChange('originalAmount', cleanedValue);
        if (formData.currency !== 'CLP' && formData.rate) {
          const localAmount = parseFloat(cleanedValue || 0) * parseFloat(formData.rate);
          handleInputChange('amount', Math.round(localAmount));
        } else {
          handleInputChange('amount', cleanedValue);
        }
      }
    }
  };

  const handleRateChange = (e) => {
    const value = e.target.value;
    handleInputChange('rate', value);
    
    // Recalculate amount if originalAmount exists
    if (formData.originalAmount) {
      const localAmount = parseFloat(formData.originalAmount || 0) * parseFloat(value || 0);
      handleInputChange('amount', Math.round(localAmount));
    }
  };

  const handleCurrencyChange = (value) => {
    handleInputChange('currency', value);
  };

  const handleObligationCurrencyChange = (value) => {
    handleInputChange('obligationCurrency', value);
    // If non-CLP currency is selected, set invoiceCurrency and clear exchange rate
    if (value !== 'CLP') {
      // For UF/UTM, set invoiceCurrency to CLP (disabled)
      // For USD/EUR, set invoiceCurrency to the selected currency
      const invoiceCurrency = (value === 'UF' || value === 'UTM') ? 'CLP' : value;
      handleInputChange('invoiceCurrency', invoiceCurrency);
      handleInputChange('invoiceExchangeRate', '');
    } else {
      // If CLP is selected, clear invoiceCurrency and exchange rate
      handleInputChange('invoiceCurrency', '');
      handleInputChange('invoiceExchangeRate', '');
    }
  };

  const handleDocumentIdChange = (e) => {
    handleInputChange('documentId', e.target.value);
  };

  const handleDescriptionChange = (e) => {
    handleInputChange('description', e.target.value);
  };

  const handleCashflowAreaChange = (value) => {
    handleInputChange('cashflowAreaId', value);
  };

  const handleFieldKeyChange = (value) => {
    handleInputChange('fieldKey', value);
  };

  const handleReferenceNumberChange = (e) => {
    handleInputChange('referenceNumber', e.target.value);
  };

  const handleInvoiceCurrencyChange = (value) => {
    handleInputChange('invoiceCurrency', value);
    // Clear exchange rate when currency changes so it can be fetched fresh
    handleInputChange('invoiceExchangeRate', '');
  };

  const handleInvoiceExchangeRateChange = (e) => {
    const value = e.target.value;
    // Allow decimals up to 2 places
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      handleInputChange('invoiceExchangeRate', value);
    }
  };

  const handleDiasVencimientoChange = (e) => {
    const value = e.target.value;
    // Allow only positive integers (0 to infinity)
    if (value === '' || /^\d+$/.test(value)) {
      handleInputChange('diasVencimiento', value);
    }
  };

  const handleFactoringNameChange = (e) => {
    handleInputChange('factoringName', e.target.value);
  };

  // Function to determine which form component to render based on obligation type
  // This allows different obligations to use different form layouts in the future
  const renderObligationFormFields = () => {
    const obligation = formData.obligation;
    
    // Check if this is an Invoice obligation (type is "invoice")
    // Check both the type property and the obligationType value, and typeLabel
    const isInvoice = obligation?.type === 'invoice' || 
                      formData.obligationType === 'invoice' ||
                      obligation?.typeLabel === 'Invoice' ||
                      obligation?.typeLabel === 'invoice';
    
    if (isInvoice) {
      return (
        <InvoiceObligationFormFields
          formData={formData}
          handleInputChange={handleInputChange}
          onSelectContraparte={onSelectContraparte}
          handleDescriptionChange={handleDescriptionChange}
          handleRecurrenceChange={handleRecurrenceChange}
          handleDesdeDateChange={handleDesdeDateChange}
          handleHastaDateChange={handleHastaDateChange}
          handleRepeatChange={handleRepeatChange}
          handleRateChange={handleRateChange}
          handleDocumentIdChange={handleDocumentIdChange}
          handleCashflowAreaChange={handleCashflowAreaChange}
          handleFieldKeyChange={handleFieldKeyChange}
          handleReferenceNumberChange={handleReferenceNumberChange}
          handleInvoiceCurrencyChange={handleInvoiceCurrencyChange}
          handleInvoiceExchangeRateChange={handleInvoiceExchangeRateChange}
          companyId={companyId}
          companyPlans={companyPlans}
          open={open}
          initialData={initialData}
          disableRecurrence={!!initialData && !obligationCopyMode}
          showMoreCampos={showMoreCampos}
          setShowMoreCampos={setShowMoreCampos}
          shouldFadeIn={shouldFadeIn}
          contraparteSessionKey={contraparteSessionKey}
        />
      );
    }
    
    // Default form for all other obligations
    return (
      <DefaultObligationFormFields
        formData={formData}
        handleInputChange={handleInputChange}
        onSelectContraparte={onSelectContraparte}
        handleDescriptionChange={handleDescriptionChange}
        handleRecurrenceChange={handleRecurrenceChange}
        handleDesdeDateChange={handleDesdeDateChange}
        handleHastaDateChange={handleHastaDateChange}
        handleRepeatChange={handleRepeatChange}
        handleRateChange={handleRateChange}
        handleDocumentIdChange={handleDocumentIdChange}
        handleCashflowAreaChange={handleCashflowAreaChange}
        handleFieldKeyChange={handleFieldKeyChange}
        companyId={companyId}
        companyPlans={companyPlans}
        open={open}
        disableRecurrence={!!initialData && !obligationCopyMode}
        handleFactoringNameChange={handleFactoringNameChange}
        showMoreCampos={showMoreCampos}
        setShowMoreCampos={setShowMoreCampos}
        shouldFadeIn={shouldFadeIn}
        contraparteSessionKey={contraparteSessionKey}
      />
    );
  };


  const manageCurrencyIcon = {
    USD: "dollar",
    EUR: "euro", 
    CLP: "dollar",
    GBP: "pound",
    MXN: "dollar",
  };


  const handleConfirm = async () => {
    if (!formData.obligation) {
      setErrorMessage('Por favor, seleccione un tipo de obligación.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const obligation = formData.obligation;
      // Get isReceived: prioritize initialData if present, otherwise use obligation's value, default to true (same logic as toggle)
      const isReceived = formData.initialIsReceived !== null
        ? formData.initialIsReceived
        : (obligation?.isReceived !== undefined 
          ? obligation.isReceived 
          : true);

      // Create agenda contact if contraparteId is 'add'
      // Don't create if RUT matches company RUT (company shouldn't be created as a counterpart)
      let createdAgendaId = null;
      const cleanedContraparteRut = formData.contraparteRut ? cleanRut(formData.contraparteRut) : '';
      const cleanedCompanyRut = companyRut ? cleanRut(companyRut) : '';
      const isCompanyRut = cleanedContraparteRut && cleanedCompanyRut && cleanedContraparteRut === cleanedCompanyRut;
      
      if (formData.contraparteId === 'add' && formData.contraparteRut && formData.contraparteName && !isCompanyRut) {
        const newAgendaContact = {
          companyId,
          isClient: !isReceived,
          isProvider: isReceived,
          name: formData.contraparteName,
          rut: cleanedContraparteRut,
          paymentDaysClient: 30,
          paymentDaysProvider: 30,
        };

        const { error: agendaError, data: createAgendaData } = await createAgenda({ input: newAgendaContact });

        if (agendaError) {
          setIsLoading(false);
          setErrorMessage('Error al crear el contacto. Por favor, intenta nuevamente.');
          return;
        }

        if (createAgendaData?.createAgenda?.status === false) {
          // Check if the error is because contact already exists
          const errorMessage = createAgendaData?.createAgenda?.message || '';
          const contactExists = errorMessage.includes('ya existe') || 
                               errorMessage.includes('already exists') ||
                               errorMessage.includes('duplicate');
          
          if (contactExists) {
            // Contact already exists - ask user to select it manually
            setIsLoading(false);
            setErrorMessage('El contacto ya existe. Por favor, selecciónalo manualmente desde el dropdown de contraparte.');
            return;
          } else {
            // Other error - show error message
            setIsLoading(false);
            setErrorMessage(errorMessage || 'Error al crear el contacto.');
            return;
          }
        } else {
          // Obtener el ID de la agenda creada para usarlo como counterpartId
          if (createAgendaData?.createAgenda?.agenda?.id) {
            createdAgendaId = createAgendaData.createAgenda.agenda.id;
          }
        }
      }

      // Determinar fecha del documento basado en recurrencia o factoring
      const isFactoring = formData.obligationType === 'ingresoporfactoring';
      let documentDate;
      let dueDate;
      let originalDueDate;
      
      if (isFactoring) {
        // Para factoring: documentDate = fecha actual + días de vencimiento
        const days = parseInt(formData.diasVencimiento || 0);
        documentDate = moment().add(days, 'days').toDate();
        dueDate = documentDate;
        originalDueDate = documentDate;
      } else {
        // Para obligaciones regulares: usar lógica de recurrencia
        documentDate = formData.recurrence === 'unica' 
          ? (formData.fecha ? moment(formData.fecha).toDate() : new Date())
          : (formData.desdeDate ? moment(formData.desdeDate).toDate() : new Date());
      }

      // Use contraparte when selected manually or auto-filled from reconcile movement
      const hasContraparte = userSelectedContraparte && formData.contraparteRut && formData.contraparteRut.trim() !== '';
      const companyIdNorm = companyRut ? rutWithDash(companyRut) : '';

      const partySource = !hasContraparte ? resolvePartySourceDocument(initialData) : null;
      const bankTxSource = !hasContraparte && !partySource ? resolvePartySourceBankTransaction(initialData) : null;
      const bankTxContraparte = bankTxSource ? extractContraparteFromBankTransaction(bankTxSource) : { rut: '', name: '' };
      const bankTxRutNorm = normalizePartyRut(bankTxContraparte.rut);
      const docIssuerRaw = partySource ? (partySource.meta?.issuerId ?? partySource.issuer ?? '') : '';
      const docReceiverRaw = partySource ? (partySource.meta?.receiverId ?? partySource.receiver ?? '') : '';
      const docIssuerId = normalizePartyRut(docIssuerRaw);
      const docReceiverId = normalizePartyRut(docReceiverRaw);
      const docIssuerName = partySource
        ? (partySource.meta?.issuerName ?? partySource.issuerName ?? '')
        : '';
      const docReceiverName = partySource
        ? (partySource.meta?.receiverName ?? partySource.receiverName ?? '')
        : '';

      let issuerId;
      let issuerName;
      let receiverId;
      let receiverName;

      if (hasContraparte) {
        issuerId = isReceived ? rutWithDash(formData.contraparteRut) : companyIdNorm;
        issuerName = isReceived ? (formData.contraparteName || '') : (companyName || '');
        receiverId = isReceived ? companyIdNorm : rutWithDash(formData.contraparteRut);
        receiverName = isReceived ? (companyName || '') : (formData.contraparteName || '');
      } else if (partySource && docIssuerId && docReceiverId) {
        issuerId = docIssuerId;
        issuerName = docIssuerName;
        receiverId = docReceiverId;
        receiverName = docReceiverName;
      } else if (partySource && docIssuerId && !docReceiverId) {
        if (isReceived) {
          issuerId = docIssuerId;
          issuerName = docIssuerName;
          receiverId = companyIdNorm;
          receiverName = companyName || '';
        } else {
          issuerId = companyIdNorm;
          issuerName = companyName || '';
          const sameCompany =
            companyRut && cleanRut(String(docIssuerRaw)) === cleanRut(companyRut);
          receiverId = sameCompany ? '' : docIssuerId;
          receiverName = sameCompany ? '' : docIssuerName;
        }
      } else if (partySource && docReceiverId && !docIssuerId) {
        receiverId = docReceiverId;
        receiverName = docReceiverName;
        if (!isReceived) {
          issuerId = companyIdNorm;
          issuerName = companyName || '';
        } else {
          issuerId = '';
          issuerName = '';
        }
      } else if (bankTxRutNorm) {
        const txName = bankTxContraparte.name || '';
        if (isReceived) {
          issuerId = bankTxRutNorm;
          issuerName = txName;
          receiverId = companyIdNorm;
          receiverName = companyName || '';
        } else {
          issuerId = companyIdNorm;
          issuerName = companyName || '';
          receiverId = bankTxRutNorm;
          receiverName = txName;
        }
      } else {
        issuerId = isReceived ? '' : companyIdNorm;
        issuerName = isReceived ? '' : (companyName || '');
        receiverId = isReceived ? companyIdNorm : '';
        receiverName = isReceived ? (companyName || '') : '';
      }

      // Parse amounts
      const originalAmount = parseFloat(formData.originalAmount || formData.amount || 0);
      const rate = parseFloat(formData.rate || 1);
      
      // Get exchange rate from invoiceExchangeRate or rate
      // Parse the exchange rate and ensure it's a valid number
      let exchangeRate = 1;
      if (formData.invoiceExchangeRate && formData.invoiceExchangeRate.trim() !== '') {
        const parsed = parseFloat(formData.invoiceExchangeRate);
        if (!isNaN(parsed) && parsed > 0) {
          exchangeRate = parsed;
        }
      } else if (formData.rate) {
        const parsed = parseFloat(formData.rate);
        if (!isNaN(parsed) && parsed > 0) {
          exchangeRate = parsed;
        }
      }
      
      // Determine currency and amounts based on whether opened from transaction
      let obligationCurrency;
      let obligationAmount;
      let metaOriginalAmount;
      let metaRate;
      let exchangeData = null;
      
      // Get the original currency (before any conversion)
      // For non-invoice obligations, use obligationCurrency if set
      const originalCurrency = formData.invoiceCurrency || 
        (formData.obligationCurrency && formData.obligationCurrency !== 'CLP' ? formData.obligationCurrency : null) ||
        formData.currency || 
        'CLP';
      
      // Check if this is an invoice obligation
      const isInvoice = obligation?.type === 'invoice' || 
                        formData.obligationType === 'invoice' ||
                        obligation?.typeLabel === 'Invoice' ||
                        obligation?.typeLabel === 'invoice';
      
      if (isInvoice) {
        // For invoice obligations: always use localValue for all amounts and CLP currency
        const localValue = originalCurrency !== 'CLP' && exchangeRate && exchangeRate !== 1
          ? Math.round(exchangeRate * originalAmount)
          : originalAmount; // If CLP or no exchange rate, use original amount
        
        obligationCurrency = 'CLP';
        obligationAmount = localValue;
        metaOriginalAmount = localValue; // Same as amount (both in CLP)
        metaRate = 1; // Rate is 1 for CLP obligations
        
        // Build exchange data - include if originalCurrency is not CLP and we have a valid exchange rate
        // Include exchange data whenever we have a non-CLP currency and an exchange rate was provided
        // (either manually entered in invoiceExchangeRate or auto-fetched and stored there, or in rate field)
        const hasExchangeRateProvided = (formData.invoiceExchangeRate && formData.invoiceExchangeRate.trim() !== '') || 
                                        (formData.rate && formData.rate !== 1 && formData.rate !== '1');
        if (originalCurrency !== 'CLP' && exchangeRate > 0 && hasExchangeRateProvided) {
          const country = getCountryFromCurrency(originalCurrency);
          exchangeData = {
            originalAmount: originalAmount, // MONTO MONEDA ORIGINAL
            originalCurrency: originalCurrency, // MONEDA
            localAmount: localValue, // VALOR BRUTO LOCAL (in CLP)
            localCurrency: 'CLP', // MONEDA LOCAL (default CLP)
            exchangeRate: exchangeRate, // TIPO DE CAMBIO
            exchangeDate: documentDate, // FECHA DE EMISIÓN
            ...(country && { country: country }), // PAÍS
          };
        }
      } else {
        // For non-invoice obligations: use existing logic
        if (initialData) {
          // Opened from transaction: convert to CLP (existing logic)
          const localValue = formData.invoiceCurrency && formData.invoiceCurrency !== 'CLP' && exchangeRate
            ? Math.round(exchangeRate * originalAmount)
            : originalAmount;
          
          obligationCurrency = formData.invoiceCurrency && formData.invoiceCurrency !== 'CLP' ? 'CLP' : (formData.currency || 'CLP');
          obligationAmount = formData.invoiceCurrency && formData.invoiceCurrency !== 'CLP' ? localValue : originalAmount;
          metaOriginalAmount = obligationAmount; // Same as amount (both in CLP)
          metaRate = 1; // Rate is 1 for CLP obligations
          
          // Build exchange data for transaction-based obligations
          if (formData.invoiceCurrency && formData.invoiceCurrency !== 'CLP' && exchangeRate) {
            const country = getCountryFromCurrency(originalCurrency);
            exchangeData = {
              originalAmount: originalAmount, // MONTO MONEDA ORIGINAL
              originalCurrency: originalCurrency, // MONEDA
              localAmount: localValue, // VALOR BRUTO LOCAL (in CLP)
              localCurrency: 'CLP', // MONEDA LOCAL (default CLP)
              exchangeRate: exchangeRate, // TIPO DE CAMBIO
              exchangeDate: documentDate, // FECHA DE EMISIÓN
              ...(country && { country: country }), // PAÍS
            };
          }
        } else {
          // NOT opened from transaction: handle non-invoice obligations with currency selection
          const selectedCurrency = formData.obligationCurrency || 'CLP';
          
          if (selectedCurrency !== 'CLP') {
            // Non-CLP currency selected: convert to CLP
            const localValue = exchangeRate && exchangeRate !== 1
              ? Math.round(exchangeRate * originalAmount)
              : originalAmount;
            
            obligationCurrency = 'CLP';
            obligationAmount = localValue;
            metaOriginalAmount = localValue; // Same as amount (both in CLP)
            metaRate = 1; // Rate is 1 for CLP obligations
            
            // Build exchange data
            // For UF/UTM, originalCurrency should be the selected currency (UF/UTM), but MONEDA in exchange should be CLP
            // For USD/EUR, originalCurrency should be the selected currency
            const exchangeOriginalCurrency = selectedCurrency; // UF, UTM, USD, or EUR
            const exchangeLocalCurrency = 'CLP'; // Always CLP for local amount
            
            if (exchangeRate && exchangeRate !== 1) {
              const country = getCountryFromCurrency(exchangeOriginalCurrency);
              exchangeData = {
                originalAmount: originalAmount, // MONTO MONEDA ORIGINAL (in UF/UTM/USD/EUR)
                originalCurrency: exchangeOriginalCurrency, // MONEDA (UF/UTM/USD/EUR)
                localAmount: localValue, // VALOR BRUTO LOCAL (in CLP)
                localCurrency: exchangeLocalCurrency, // MONEDA LOCAL (CLP)
                exchangeRate: exchangeRate, // TIPO DE CAMBIO
                exchangeDate: documentDate, // FECHA DE EMISIÓN
                ...(country && { country: country }), // PAÍS
              };
            }
          } else {
            // CLP selected: keep original currency and amounts
            obligationCurrency = 'CLP';
            obligationAmount = originalAmount; // Keep original amount
            metaOriginalAmount = originalAmount; // Same as amount (in CLP)
            metaRate = 1; // Rate is 1 for CLP obligations
          }
        }
      }

      // Build meta object
      const meta = {
        family: obligation.family,
        familyLabel: obligation.familyLabel,
        type: obligation.type,
        typeLabel: obligation.typeLabel,
        description: formData.description || '',
        date: documentDate,
        documentDate: documentDate,
        amount: obligationAmount,
        currency: obligationCurrency,
        remaining: obligationAmount, // Use obligationAmount (localValue for non-CLP, originalAmount for CLP)
        isPaid: false,
        originalAmount: metaOriginalAmount, // Same as amount (both in CLP for obligations)
        taxAmount: 0,
        taxRate: 0,
        issuerId: issuerId,
        issuerName: issuerName,
        receiverId: receiverId,
        receiverName: receiverName,
        isReceived: isReceived,
        rate: metaRate, // Use metaRate (exchange rate if not from transaction, 1 if from transaction)
        isValid: true,
        creationFor: obligationCopyMode ? 'free' : initialData ? 'match' : 'free',
        ...(isPaymentMethod || formData.paymentMethod ? { paymentMethod: true } : {}),
      };
      
      // For factoring, include factoring name in description if provided
      // Note: factoring field is not part of ObligationMetaInput, so we store it in description
      if (isFactoring && formData.factoringName) {
        meta.description = meta.description 
          ? `${meta.description} - Factoring: ${formData.factoringName}`
          : `Factoring: ${formData.factoringName}`;
      }
      
      // Add exchange data if available
      if (exchangeData) {
        meta.exchange = exchangeData;
      }

      // Handle recurrence
      if (formData.recurrence === 'recurrente') {
        meta.desdeDate = formData.desdeDate ? moment(formData.desdeDate).toDate() : undefined;
        meta.hastaDate = formData.hastaDate ? moment(formData.hastaDate).toDate() : undefined;
        meta.repeat = formData.repeat || 30;
        meta.tipo = 'recurrente';
      }

      // Optional fields
      if (formData.documentId) {
        meta.documentId = formData.documentId;
        meta.number = formData.documentId;
      }

      // Reference number for invoices (when currency is not CLP)
      if (formData.referenceNumber) {
        meta.referenceNumber = formData.referenceNumber;
      }

      // Guardar counterpartId (ID de agenda) si hay una contraparte
      // Usar createdAgendaId si se acaba de crear una agenda, de lo contrario usar el contraparteId disponible
      if (createdAgendaId) {
        meta.counterpartId = createdAgendaId;
      } else if (formData.contraparteId && formData.contraparteId !== 'add') {
        // Guardar counterpartId si hay un ID válido (ya sea seleccionado o pre-llenado)
        meta.counterpartId = formData.contraparteId;
      }

      // Garantía obligations: store login / transaction number / ticket in meta
      const isGarantia = formData.obligationType === 'garantiaentransito' ||
        formData.obligationType === 'garantiadeclientes' ||
        formData.obligationType === 'garantiaportransbank' ||
        formData.obligationType === 'forward' ||
        formData.obligationType === 'garantiabroker' ||
        formData.obligationType === 'rescatedegarantia';
      if (isGarantia) {
        meta.login = formData.login || '';
        meta.transactionNumber = formData.transactionNumber || '';
        meta.ticket = formData.ticket || '';
      }

      // Build obligation document input
      const obligationInput = {
        companyId: companyId,
        documentType: 'OBLIGATION',
        amount: obligationAmount, // Use obligationAmount (localValue for non-CLP, originalAmount for CLP)
        currencyType: obligationCurrency, // Use CLP for non-CLP transactions, otherwise use formData.currency
        isManual: true,
        meta: meta,
        documentDate: documentDate,
      };

      // Add dueDate and originalDueDate for factoring
      if (isFactoring && dueDate && originalDueDate) {
        obligationInput.dueDate = dueDate;
        obligationInput.originalDueDate = originalDueDate;
      }

      if (formData.cashflowAreaId) {
        obligationInput.cashflowAreaId = formData.cashflowAreaId;
      }

      if (formData.fieldKey) {
        obligationInput.fieldKey = formData.fieldKey;
      }

      // Call the mutation
      const { data, error } = await executeMutation({ 
        input: obligationInput, 
        isDte: false 
      });

      if (error) {
        setIsLoading(false);
        console.error('Error creating obligation:', error);
        // Show generic error message to user
        setErrorMessage('Error al crear la obligación. Por favor, verifica que todos los campos estén completos e intenta nuevamente.');
        return;
      }

      if (data && data.createObligationDocument) {
        // Success
        setErrorMessage('');

        const obligationId = data.createObligationDocument.id;

        // If initialData exists (called from reconcile), automatically match with the obligation
        // Check if initialData is multiple transactions, single transaction, or document
        if (initialData && !obligationCopyMode) {
          // Check if this is multiple transactions
          const isMultipleTransactions = initialData.isMultiple === true || 
                                         (initialData.selectedTransactions && 
                                          Array.isArray(initialData.selectedTransactions) && 
                                          initialData.selectedTransactions.length > 1);
          
          // Check if this is multiple documents
          const isMultipleDocuments = initialData.isMultipleDocuments === true ||
                                     (initialData.selectedDocuments &&
                                      Array.isArray(initialData.selectedDocuments) &&
                                      initialData.selectedDocuments.length > 1);
          
          if (isMultipleDocuments) {
            // Handle multiple documents - match all to single obligation
            const selectedDocuments = initialData.selectedDocuments || [];
            
            try {
              // Calculate currency, exchange rate, and local value for obligation
              const originalCurrency = formData.invoiceCurrency || formData.currency || 'CLP';
              const exchangeRate = formData.invoiceExchangeRate 
                ? parseFloat(formData.invoiceExchangeRate) 
                : 1;
              const matchCurrency = 'CLP';
              
              // Build origin array with single obligation
              const origin = [{
                originType: "OBLIGATION",
                originId: obligationId,
                amount: obligationAmount,
                currency: matchCurrency,
                originalAmount: metaOriginalAmount,
                exchangeRate: exchangeRate,
                localValue: obligationAmount,
                matchCurrency: matchCurrency,
                paymentDate: documentDate,
              }];
              
              // Build end array with all documents
              const end = selectedDocuments.map(doc => {
                const documentId = doc.id || doc._id;
                const documentCurrency = doc.currencyType || doc.currency || 'CLP';
                const documentRemaining = doc.meta?.remaining !== undefined 
                  ? doc.meta.remaining 
                  : (doc.remaining !== undefined ? doc.remaining : (doc.amount || doc.meta?.amount || 0));
                
                // Calculate local value for document if it has non-CLP currency
                let documentLocalValue = Math.abs(documentRemaining);
                let documentExchangeRate = 1;
                
                if (documentCurrency !== 'CLP' && exchangeRate && exchangeRate !== 1) {
                  if (documentCurrency === originalCurrency) {
                    documentLocalValue = Math.round(exchangeRate * Math.abs(documentRemaining));
                    documentExchangeRate = exchangeRate;
                  } else {
                    documentLocalValue = Math.abs(documentRemaining);
                  }
                }
                
                return {
                  endType: doc.documentType || 'TAX_DOCUMENT',
                  endId: documentId,
                  amount: documentLocalValue,
                  currency: documentCurrency,
                  originalAmount: Math.abs(documentRemaining),
                  exchangeRate: documentExchangeRate,
                  localValue: documentLocalValue,
                  matchCurrency: matchCurrency,
                };
              });
              
              const multipleMatchInput = {
                companyId: companyId,
                accountPlan: "NOPLAN",
                origin: origin,
                end: end,
              };
              
              const { error: matchError } = await generateMultipleMatch({ input: multipleMatchInput });
              
              if (matchError) {
                console.error('Error matching obligation with multiple documents:', matchError);
                dispatch(
                  importAlertState({
                    content: 'Obligación creada, pero hubo un error al conciliarla con los documentos.',
                    state: 'warning',
                    duration: 4,
                  })
                );
              } else {
                dispatch(setUpdateDocuments({ value: true }));
              }
            } catch (matchErr) {
              console.error('Error matching obligation with multiple documents:', matchErr);
              dispatch(
                importAlertState({
                  content: 'Obligación creada, pero hubo un error al conciliarla con los documentos.',
                  state: 'warning',
                  duration: 4,
                })
              );
            }
          } else if (isMultipleTransactions) {
            // Handle multiple transactions - match all to single obligation
            const selectedTransactions = initialData.selectedTransactions || [];
            
            try {
              // Calculate currency, exchange rate, and local value for obligation
              // Get original currency from invoiceCurrency, currency, or default to CLP
              const originalCurrency = formData.invoiceCurrency || formData.currency || 'CLP';
              // Get exchange rate from invoiceExchangeRate, or default to 1
              const exchangeRate = formData.invoiceExchangeRate 
                ? parseFloat(formData.invoiceExchangeRate) 
                : 1;
              // Match currency should always be CLP (the obligation currency)
              const matchCurrency = 'CLP';
              
              // Build origin array with all transactions
              const origin = selectedTransactions.map(transaction => {
                const transactionId = transaction.id || transaction._id;
                const transactionCurrency = transaction.currency || 'CLP';
                const transactionRemaining = transaction.remaining !== undefined 
                  ? transaction.remaining 
                  : (transaction.meta?.remaining !== undefined ? transaction.meta?.remaining : (transaction.amount || transaction.meta?.amount || 0));
                
                // Calculate local value for this transaction if it has non-CLP currency
                let transactionLocalValue = transactionRemaining;
                let transactionExchangeRate = 1;
                
                if (transactionCurrency !== 'CLP' && exchangeRate && exchangeRate !== 1) {
                  // If transaction currency matches the obligation's original currency, use the same exchange rate
                  if (transactionCurrency === originalCurrency) {
                    transactionLocalValue = Math.round(exchangeRate * Math.abs(transactionRemaining));
                    transactionExchangeRate = exchangeRate;
                  } else {
                    // Different currency - for now, use the transaction amount as-is
                    // In a more sophisticated implementation, we'd fetch exchange rate for this currency
                    transactionLocalValue = Math.abs(transactionRemaining);
                  }
                } else {
                  transactionLocalValue = Math.abs(transactionRemaining);
                }
                
                return {
                  originType: "TRANSACTION",
                  originId: transactionId,
                  amount: transactionLocalValue, // Local value in CLP
                  currency: transactionCurrency, // Original transaction currency
                  originalAmount: Math.abs(transactionRemaining), // Original amount in transaction currency
                  exchangeRate: transactionExchangeRate,
                  localValue: transactionLocalValue,
                  matchCurrency: matchCurrency,
                  paymentDate: documentDate,
                };
              });
              
              // Build end array with single obligation
              const end = [{
                endType: "OBLIGATION",
                endId: obligationId,
                amount: obligationAmount, // Obligation amount in CLP
                currency: matchCurrency, // CLP (obligation currency)
                originalAmount: metaOriginalAmount, // Same as amount (both in CLP)
              }];
              
              const multipleMatchInput = {
                companyId: companyId,
                accountPlan: "NOPLAN",
                origin: origin,
                end: end,
              };
              
              const { error: matchError } = await generateMultipleMatch({ input: multipleMatchInput });
              
              if (matchError) {
                console.error('Error matching obligation with multiple transactions:', matchError);
                dispatch(
                  importAlertState({
                    content: 'Obligación creada, pero hubo un error al conciliarla con las transacciones.',
                    state: 'warning',
                    duration: 4,
                  })
                );
              } else {
                // Dispatch updates to refresh the transactions view
                dispatch(importUpdateTransactions(true));
              }
            } catch (matchErr) {
              console.error('Error matching obligation with multiple transactions:', matchErr);
              dispatch(
                importAlertState({
                  content: 'Obligación creada, pero hubo un error al conciliarla con las transacciones.',
                  state: 'warning',
                  duration: 4,
                })
              );
            }
          } else {
            // Handle single transaction or document (existing logic)
            const initialDataId = initialData.id || initialData._id;
            if (initialDataId) {
              // Determine if initialData is a transaction or document (declare before try for catch block access)
              const isInitialDataTransaction = isBankTransactionType(initialData);
              const isInitialDataDocument = isSourceDocumentRow(initialData);
              
              try {
                
                // Calculate currency, exchange rate, and local value
                // Get original currency from invoiceCurrency, currency, or default to CLP
                const originalCurrency = formData.invoiceCurrency || formData.currency || 'CLP';
                // Get exchange rate from invoiceExchangeRate, or default to 1
                const exchangeRate = formData.invoiceExchangeRate 
                  ? parseFloat(formData.invoiceExchangeRate) 
                  : 1;
                // Calculate local value (exchangeRate * originalAmount, rounded to no decimals)
                const localValue = originalCurrency !== 'CLP' && exchangeRate
                  ? Math.round(exchangeRate * originalAmount)
                  : originalAmount;
                // Use localValue as the amount for the match
                const matchAmount = localValue;
                // Match currency should always be CLP (the obligation currency)
                const matchCurrency = 'CLP';

                // Format for GenerateMultipleMatchInput
                // Determine origin and end based on initialData type
                let multipleMatchInput;
                
                if (isInitialDataTransaction) {
                  // Transaction is origin, obligation is end (existing logic)
                  multipleMatchInput = {
                    companyId: companyId,
                    accountPlan: "NOPLAN",
                    origin: [{
                      originType: "TRANSACTION",
                      originId: initialDataId,
                      amount: matchAmount, // Use localValue as amount (in CLP)
                      currency: initialData.currency || 'CLP', // Transaction currency
                      originalAmount: originalAmount, // Original amount in original currency (for transaction remaining)
                      // Add fields that will be preserved when merged into GenerateMatchInput
                      exchangeRate: exchangeRate,
                      localValue: localValue,
                      matchCurrency: matchCurrency,
                      paymentDate: documentDate,
                    }],
                    end: [{
                      endType: "OBLIGATION",
                      endId: obligationId,
                      amount: matchAmount, // Use localValue as amount (in CLP)
                      currency: matchCurrency, // CLP (obligation currency)
                      originalAmount: metaOriginalAmount, // Same as amount (both in CLP)
                    }],
                  };
                } else if (isInitialDataDocument) {
                  // Document is end, obligation is origin (reversed logic)
                  const documentType = initialData.documentType || 'OBLIGATION';
                  const documentCurrency = initialData.currencyType || initialData.currency || 'CLP';
                  const documentRemaining = initialData.meta?.remaining !== undefined 
                    ? initialData.meta.remaining 
                    : (initialData.remaining !== undefined ? initialData.remaining : (initialData.amount || initialData.meta?.amount || 0));
                  
                  multipleMatchInput = {
                    companyId: companyId,
                    accountPlan: "NOPLAN",
                    origin: [{
                      originType: "OBLIGATION",
                      originId: obligationId,
                      amount: matchAmount, // Use localValue as amount (in CLP)
                      currency: matchCurrency, // CLP (obligation currency)
                      originalAmount: metaOriginalAmount, // Same as amount (both in CLP)
                      exchangeRate: exchangeRate,
                      localValue: localValue,
                      matchCurrency: matchCurrency,
                      paymentDate: documentDate,
                    }],
                    end: [{
                      endType: documentType, // Use document's documentType (e.g., "OBLIGATION", "TAX_DOCUMENT")
                      endId: initialDataId,
                      amount: matchAmount, // Use localValue as amount (in CLP)
                      currency: documentCurrency, // Document currency
                      originalAmount: documentRemaining, // Document remaining amount
                    }],
                  };
                } else {
                  // Fallback: mongo id without bank-transaction shape → match as document
                  const fallbackDocumentType =
                    initialData.documentType ||
                    initialData.meta?.documentType ||
                    'TAX_DOCUMENT';
                  const documentCurrency =
                    initialData.currencyType || initialData.currency || 'CLP';
                  const documentRemaining =
                    initialData.meta?.remaining !== undefined
                      ? initialData.meta.remaining
                      : (initialData.remaining !== undefined
                        ? initialData.remaining
                        : (initialData.amount || initialData.meta?.amount || 0));

                  multipleMatchInput = {
                    companyId: companyId,
                    accountPlan: "NOPLAN",
                    origin: [{
                      originType: "OBLIGATION",
                      originId: obligationId,
                      amount: matchAmount,
                      currency: matchCurrency,
                      originalAmount: metaOriginalAmount,
                      exchangeRate: exchangeRate,
                      localValue: localValue,
                      matchCurrency: matchCurrency,
                      paymentDate: documentDate,
                    }],
                    end: [{
                      endType: fallbackDocumentType,
                      endId: initialDataId,
                      amount: matchAmount,
                      currency: documentCurrency,
                      originalAmount: documentRemaining,
                    }],
                  };
                }

                const { error: matchError } = await generateMultipleMatch({ input: multipleMatchInput });

                if (matchError) {
                  console.error('Error matching obligation:', matchError);
                  const errorMessage = isInitialDataTransaction 
                    ? 'Obligación creada, pero hubo un error al conciliarla con la transacción.'
                    : 'Obligación creada, pero hubo un error al conciliarla con el documento.';
                  dispatch(
                    importAlertState({
                      content: errorMessage,
                      state: 'warning',
                      duration: 4,
                    })
                  );
                } else {
                  // Dispatch updates to refresh the reconcile view
                  if (isInitialDataTransaction) {
                    dispatch(importUpdateTransactions(true));
                  } else {
                    dispatch(setUpdateDocuments({ value: true }));
                  }
                }
              } catch (matchErr) {
                console.error('Error matching obligation:', matchErr);
                const errorMessage = isInitialDataTransaction 
                  ? 'Obligación creada, pero hubo un error al conciliarla con la transacción.'
                  : 'Obligación creada, pero hubo un error al conciliarla con el documento.';
                dispatch(
                  importAlertState({
                    content: errorMessage,
                    state: 'warning',
                    duration: 4,
                  })
                );
              }
            }
          }
        }

        // Dispatch update actions to refresh cashflow, EERR, and Proveedores components
        dispatch(setUpdateDocuments({ value: true }));
        dispatch(importUpdateCashFlow(true));
        dispatch(importUpdateCashflowNeeded({ value: true, reload: null }));
        dispatch(importEERRUpdateNeeded({ value: true, reload: null }));
        dispatch(importUpdateProvidersData(true));

        // Call onSuccess callback if provided (e.g., to clear selected transactions)
        if (onSuccess) {
          onSuccess();
        }

        // Keep loading state until modal closes
        // Close the modal immediately after successful creation
        onClose();
      } else {
        setIsLoading(false);
        setErrorMessage('Error al crear la obligación. Por favor, verifica que todos los campos estén completos e intenta nuevamente.');
      }
    } catch (err) {
      setIsLoading(false);
      console.error('Error creating obligation:', err);
      setErrorMessage('Error inesperado. Por favor, intenta nuevamente.');
    }
  };

  const handleCancel = () => {
    setFormData({
      obligationType: '',
      obligation: null,
      recurrence: 'unica',
      fecha: moment().format('YYYY-MM-DD'),
      desdeDate: '',
      hastaDate: '',
      repeat: 30,
      amount: '',
      originalAmount: '',
      currency: 'CLP',
      rate: 1,
      contraparte: '',
      contraparteId: '',
      contraparteRut: '',
      contraparteName: '',
      documentId: '',
      description: '',
      cashflowAreaId: '',
        fieldKey: '',
        referenceNumber: '',
        invoiceCurrency: '',
        invoiceExchangeRate: '',
        initialIsReceived: null,
        obligationCurrency: 'CLP',
        paymentMethod: false,
        diasVencimiento: '30',
        factoringName: '',
        login: '',
        transactionNumber: '',
        ticket: ''
      });
    setErrorMessage('');
    setIsLoading(false);
    setUserSelectedContraparte(false);
    setShowMoreCampos(false);
    onClose();
  };

  const isFactoring = formData.obligationType === 'ingresoporfactoring';
  const requiresCashflowArea = formData.obligationType === 'estimaciondeingreso' || formData.obligationType === 'estimaciondeegreso';
  const isGarantiaDeClientes = formData.obligationType === 'garantiadeclientes' ||
    formData.obligationType === 'garantiaportransbank' ||
    formData.obligationType === 'rescatedegarantia';

  const isFormValid = formData.obligationType &&
    (formData.amount || formData.originalAmount) &&
    (!isGarantiaDeClientes || (formData.login && formData.transactionNumber && formData.ticket)) &&
    // For factoring: require diasVencimiento and factoringName, skip fecha requirement
    (isFactoring 
      ? (formData.diasVencimiento && formData.factoringName)
      : (formData.recurrence === 'unica' ? formData.fecha : (formData.desdeDate && formData.hastaDate && formData.repeat))
    ) &&
    // Contraparte is optional - only require rut and name if user is creating a new one (contraparteId === 'add')
    // Skip contraparte validation for factoring
    (isFactoring || !formData.contraparteId || formData.contraparteId !== 'add' || (formData.contraparteRut && formData.contraparteName)) &&
    // Require cashflowAreaId for estimaciondeingreso and estimaciondeegreso
    (!requiresCashflowArea || formData.cashflowAreaId);

  // Get isReceived value: prioritize initialData if present, otherwise use obligation's value, default to true (Por Pagar)
  const isReceived = formData.initialIsReceived !== null
    ? formData.initialIsReceived
    : (formData.obligation?.isReceived !== undefined 
      ? formData.obligation.isReceived 
      : true);

  // Check if toggle should be disabled
  const isToggleDisabled =
    (!!initialData && !obligationCopyMode) ||
    !formData.obligation ||
    !formData.obligation?.allowedToggle;
  // Tooltip message when disabled due to allowedToggle being false
  const toggleTooltip = !formData.obligation?.allowedToggle && formData.obligation
    ? t('obligationModal.toggleDisabledTooltip')
    : null;

  return (
    <CustomModal
      open={open}
      onClose={handleCancel}
      title={
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', position: 'relative' }}>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <span>{obligationCopyMode ? t('obligationModal.titleCopy') : t('obligationModal.titleNew')}</span>
          </div>
          <div 
            style={{ position: 'absolute', right: 0 }}
            title={toggleTooltip || undefined}
          >
            <PorCobrarPorPagarToggle
              value={isReceived}
              onChange={handleIsReceivedToggle}
              disabled={isToggleDisabled}
            />
          </div>
        </div>
      }
      classNameModal="create-obligation-modal"
      content={
        <div className="create-obligation-modal-content">
          {errorMessage && (
            <div className="obligation-error-message scfo-alert--error">
              <Icon name="exclamation triangle" color="red" />
              {errorMessage}
            </div>
          )}
          <div className="obligation-modal-sections">
            {/* Left Section */}
            <div className="obligation-left-section">
              {/* Información General Section */}
              <div className="obligation-section-title">{t('obligationModal.generalInfo')}</div>
              
              {/* Obligación a crear, FECHA, and MONTO in same row - OBLIGACIÓN A CREAR is 50% */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px' }}>
                <div className="obligation-field">
                  <label className="obligation-label" htmlFor="obligation-type">{t('obligationModal.obligationToCreate')}<span className="required-asterisk">*</span></label>
                  <ObligationsDropdown
                    id="obligation-type"
                    value={formData.obligationType}
                    type={preSelectedObligationType || undefined}
                    onChange={handleObligationTypeChange}
                    placeholder={t('obligationModal.selectObligationType')}
                    className="w-100"
                    disabled={Boolean(isPaymentMethod)}
                  />
                </div>
                  <div className="obligation-field">
                    {(() => {
                      const isFactoring = formData.obligationType === 'ingresoporfactoring';
                      if (isFactoring) {
                        return (
                          <>
                            <label className="obligation-label" htmlFor="diasVencimiento">{t('obligationModal.dueDays')}<span className="required-asterisk">*</span></label>
                            <input
                              id="diasVencimiento"
                              type="number"
                              min="0"
                              step="1"
                              value={formData.diasVencimiento || ""}
                              onChange={handleDiasVencimientoChange}
                              placeholder={t('obligationModal.enterDays')}
                              required
                            />
                          </>
                        );
                      }
                      return (
                        <>
                          <label className="obligation-label" htmlFor="fecha">
                            {t('obligationModal.paymentDate')}
                            {formData.recurrence === 'unica' && <span className="required-asterisk">*</span>}
                          </label>
                          <input
                            id="fecha"
                            type="date"
                            value={formData.fecha}
                            onChange={handleFechaChange}
                            placeholder={t('obligationModal.selectDate')}
                            disabled={formData.recurrence === 'recurrente'}
                            required={formData.recurrence === 'unica'}
                            style={{
                              opacity: formData.recurrence === 'recurrente' ? 0.6 : 1,
                              cursor: formData.recurrence === 'recurrente' ? 'not-allowed' : 'pointer'
                            }}
                          />
                        </>
                      );
                    })()}
                  </div>
                <div className="obligation-field">
                  <label className="obligation-label" htmlFor="amount">
                    {(() => {
                      const isInvoice = formData.obligation?.type === 'invoice' || formData.obligationType === 'invoice';
                      const isNonCLPTransaction = initialData && formData.currency && formData.currency !== 'CLP';
                      
                      if (isInvoice || isNonCLPTransaction) {
                        // Get currency from invoiceCurrency, currency, or obligation
                        const currency = formData.invoiceCurrency || formData.currency || 'USD';
                        return t('obligationModal.amountOriginalCurrency', { currency });
                      }
                      return t('obligationModal.amount');
                    })()}
                    <span className="required-asterisk">*</span>
                  </label>
                  <div className={`obligation-amount-input-wrapper ${(() => {
                    const isInvoice = formData.obligation?.type === 'invoice' || formData.obligationType === 'invoice';
                    return isInvoice ? 'no-currency-selector' : '';
                  })()}`}>
                    {/* Currency selector for non-invoice obligations - attached to input */}
                    {(() => {
                      const isInvoice = formData.obligation?.type === 'invoice' || formData.obligationType === 'invoice';
                      // Show dropdown for non-invoice obligations (even when initialData is present)
                      if (!isInvoice) {
                        return (
                          <ObligationCurrencySelector
                            value={formData.obligationCurrency || formData.currency || "CLP"}
                            onChange={handleObligationCurrencyChange}
                          />
                        );
                      }
                      return null;
                    })()}
                    <input 
                      id="amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.originalAmount || ""}
                      onChange={handleAmountChange}
                      placeholder={t('obligationModal.enterAmount')}
                      inputMode="decimal"
                      required
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>
              </div>

              {/* Show existing obligation if found (only when opened from reconcile, not from payment method) */}
              {initialData && open && obligationType && period && !isPaymentMethod && (
                <div className="obligation-field" style={{ marginTop: '16px', marginBottom: '16px' }}>
                  {false ? (
                    <div style={{ padding: '12px', textAlign: 'center', color: '#6B7280' }}>
                      Buscando obligación existente...
                    </div>
                  ) : foundExistingObligation ? (
                    <div style={{ marginBottom: '16px' }}>
                      <label className="obligation-label" style={{ marginBottom: '4px', display: 'block' }}>
                        {t('obligationModal.existingPayableFound')}
                      </label>
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#6B7280', 
                        marginBottom: '8px'
                      }}>
                        {t('obligationModal.possibleMatch')}
                      </div>
                      <WeakCandidate
                        candidate={foundExistingObligation}
                        index={0}
                        matchContainer={{}}
                        editData={initialData}
                        history={history}
                        location={location}
                        generateMatch={generateMatch}
                        generateMatchFetching={fetching}
                        newAmount={parseFloat(formData.amount || formData.originalAmount || 0)}
                        newCurrency={formData.currency || 'CLP'}
                        showAmountComparison={true}
                        modifyDocument={modifyDocument}
                        companyId={companyId}
                        onReconcileSuccess={() => {
                          // Close modal after successful match
                          onClose();
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              )}

              {/* Currency/Exchange rate row - Show when opened from reconcile with non-CLP currency */}
              {initialData && formData.currency && formData.currency !== 'CLP' && (
                <CurrencyChangeRow
                  formData={{
                    ...formData,
                    invoiceCurrency: formData.invoiceCurrency || formData.currency
                  }}
                  handleInvoiceCurrencyChange={handleInvoiceCurrencyChange}
                  handleInvoiceExchangeRateChange={handleInvoiceExchangeRateChange}
                  className="margin-top-16"
                  disabled={true}
                  showReconciliationText={true}
                />
              )}

              {/* Non-invoice + moneda ≠ CLP: siempre mostrar TC (también desde conciliación). */}
              {(() => {
                const isInvoice = formData.obligation?.type === 'invoice' || formData.obligationType === 'invoice';
                const hasNonCLPCurrency = formData.obligationCurrency && formData.obligationCurrency !== 'CLP';
                // Evitar duplicar el CurrencyChangeRow de tx bancaria ya en moneda extranjera.
                const alreadyShowingTxFx = Boolean(
                  initialData && formData.currency && formData.currency !== 'CLP'
                );

                if (!isInvoice && hasNonCLPCurrency && !alreadyShowingTxFx) {
                  // For UF/UTM, MONEDA should be disabled with value CLP
                  // For USD/EUR, MONEDA should match the selected currency
                  const invoiceCurrency = (formData.obligationCurrency === 'UF' || formData.obligationCurrency === 'UTM') 
                    ? 'CLP' 
                    : formData.obligationCurrency;
                  
                  return (
                    <CurrencyChangeRow
                      formData={{
                        ...formData,
                        invoiceCurrency: invoiceCurrency
                      }}
                      handleInvoiceCurrencyChange={handleInvoiceCurrencyChange}
                      handleInvoiceExchangeRateChange={handleInvoiceExchangeRateChange}
                      className="margin-top-16"
                      disabled={formData.obligationCurrency === 'UF' || formData.obligationCurrency === 'UTM'}
                      disableExchangeRate={false}
                      obligationCurrency={formData.obligationCurrency}
                      showReconciliationText={Boolean(initialData)}
                    />
                  );
                }
                return null;
              })()}

              {/* Render obligation-specific form fields */}
              {/* Use key to force re-render when obligation type changes */}
              <div key={formData.obligationType || 'default'}>
                {renderObligationFormFields()}
              </div>
            </div>
          </div>
        </div>
      }
      confirmLabel={t('obligationModal.createPayable')}
      confirmDisabled={!isFormValid || isLoading}
      isLoading={isLoading}
      cancelarLabel={t('common.cancel')}
      onConfirm={handleConfirm}
      size="tiny"
      classNameTitle="!tw-text-center !tw-text-[16px] !tw-font-semibold"
      dimmer={{ style: { zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.5)' } }}
      modalStyle={{ 
        // width: '1300px',
        maxWidth: '95%', 
        minWidth: '1300px', 
        minHeight: '80vh',
        maxHeight: '80vh', 
        overflow: 'hidden',
        margin: '0 auto'
      }}
    />
  );
};

export default CreateObligationModal;
